import { Sliders, Wand2, Settings } from 'lucide-react';
import useAdvancedEditorStore from '../../store/useAdvancedEditorStore';
import { useEffect, useState } from 'react';
import {
  applyFeathering,
  expandMask,
  smoothMask,
  cloneImageData,
} from '../../utils/imageProcessing';

/**
 * Painel de controle lateral
 *
 * Inclui:
 * - Controle de tamanho do brush
 * - Sliders de refinamento (feather, smooth, expand)
 * - Controle de zoom
 */
const AdvancedControls = ({ onReprocess }) => {
  const {
    currentTool,
    brushSize,
    setBrushSize,
    magicWandTolerance,
    setMagicWandTolerance,
    settings,
    updateSettings,
    processingSettings,
    updateProcessingSettings,
    zoom,
    setZoom,
    mask,
    setMask,
    history,
    historyIndex,
  } = useAdvancedEditorStore();

  const [localSettings, setLocalSettings] = useState(settings);
  const [localProcessingSettings, setLocalProcessingSettings] = useState(processingSettings);

  /**
   * Aplica refinamentos quando os settings mudam
   */
  useEffect(() => {
    if (!mask || historyIndex < 0) return;

    // Pega a máscara base (primeira do histórico ou atual sem refinamentos)
    const baseMask = history[0] || mask;

    // Aplica refinamentos em ordem
    let refinedMask = cloneImageData(baseMask);

    if (localSettings.expand !== 0) {
      refinedMask = expandMask(refinedMask, localSettings.expand);
    }

    if (localSettings.feather > 0) {
      refinedMask = applyFeathering(refinedMask, localSettings.feather);
    }

    if (localSettings.smooth > 0) {
      refinedMask = smoothMask(refinedMask, localSettings.smooth);
    }

    // Atualiza máscara sem adicionar ao histórico
    setMask(refinedMask, false);
  }, [localSettings]);

  /**
   * Handler para mudança de settings
   */
  const handleSettingChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    updateSettings(newSettings);
  };

  /**
   * Handler para mudança de configurações de processamento
   */
  const handleProcessingSettingChange = (key, value) => {
    const newSettings = { ...localProcessingSettings, [key]: value };
    setLocalProcessingSettings(newSettings);
    updateProcessingSettings(newSettings);
  };

  /**
   * Handler para reprocessar imagem com novas configurações
   */
  const handleReprocess = () => {
    if (onReprocess) {
      onReprocess();
    }
  };

  return (
    <div className="w-80 bg-white border-l border-gray-200 p-6 overflow-y-auto">
      <div className="space-y-6">
        {/* Seção: Brush */}
        {(currentTool === 'brush' || currentTool === 'eraser') && (
          <Section title="Pincel" icon={Sliders}>
            <Slider
              label="Tamanho"
              value={brushSize}
              onChange={setBrushSize}
              min={1}
              max={100}
              suffix="px"
            />
          </Section>
        )}

        {/* Seção: Varinha Mágica */}
        {(currentTool === 'magic-wand-add' || currentTool === 'magic-wand-remove') && (
          <Section title="Varinha Mágica" icon={Wand2}>
            <Slider
              label="Tolerância"
              value={magicWandTolerance}
              onChange={setMagicWandTolerance}
              min={0}
              max={100}
              suffix="%"
              description="Sensibilidade da seleção de áreas similares"
            />
          </Section>
        )}

        {/* Seção: Configurações Avançadas */}
        <Section title="Refinamento IA" icon={Settings}>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="alphaMatting"
                checked={localProcessingSettings.alphaMatting}
                onChange={(e) => handleProcessingSettingChange('alphaMatting', e.target.checked)}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <label htmlFor="alphaMatting" className="text-sm font-medium text-gray-700">
                Alpha Matting
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Refina as bordas do recorte (mais lento, melhor qualidade)
            </p>

            {localProcessingSettings.alphaMatting && (
              <>
                <Slider
                  label="Foreground Threshold"
                  value={localProcessingSettings.alphaMattingForegroundThreshold}
                  onChange={(v) => handleProcessingSettingChange('alphaMattingForegroundThreshold', v)}
                  min={0}
                  max={255}
                  description="Limiar para pixels de primeiro plano"
                />

                <Slider
                  label="Background Threshold"
                  value={localProcessingSettings.alphaMattingBackgroundThreshold}
                  onChange={(v) => handleProcessingSettingChange('alphaMattingBackgroundThreshold', v)}
                  min={0}
                  max={255}
                  description="Limiar para pixels de fundo"
                />

                <Slider
                  label="Erode Size"
                  value={localProcessingSettings.alphaMattingErodeSize}
                  onChange={(v) => handleProcessingSettingChange('alphaMattingErodeSize', v)}
                  min={0}
                  max={40}
                  description="Tamanho da erosão da máscara"
                />
              </>
            )}

            <button
              onClick={handleReprocess}
              className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Reprocessar Imagem
            </button>
          </div>
        </Section>

      </div>
    </div>
  );
};

/**
 * Componente de seção
 */
const Section = ({ title, icon: Icon, children }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Icon className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

/**
 * Componente de slider
 */
const Slider = ({ label, value, onChange, min, max, suffix = '', description }) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm text-gray-600">
          {value}
          {suffix}
        </span>
      </div>

      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
      />

      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
};

export default AdvancedControls;
